import {buildForEditor as buildEditorConfig} from '../wysiwyg-tinymce/config';
import {Component} from './component';

function randomId() {
    return Math.random().toString(36).slice(2, 12);
}

function escapeHtml(text) {
    return text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
}

function createGenericSection() {
    return {
        id: randomId(),
        type: 'generic',
        minimised: false,
        showOptions: false,
        data: {
            html: '',
        },
    };
}

function createColouredSection() {
    return {
        id: randomId(),
        type: 'section',
        minimised: false,
        showOptions: false,
        data: {
            title: '',
            titleFootnote: '',
            color: '1',
            excerpt: '',
            html: '',
            isMinimised: true,
        },
    };
}

export class CofeSectionEditor extends Component {

    setup() {
        this.elem = this.$el;
        this.sectionsContainer = this.$refs.sections;
        this.subtitleInput = this.$refs.subtitle;
        this.emptyMessage = this.$refs.empty;
        this.input = this.$refs.input;
        this.legacyInput = this.$refs.legacyInput;
        this.subtitle = '';
        this.sections = [];
        this.editorMap = new Map();

        this.tinyMceOptions = {
            language: this.$opts.language,
            containerElement: this.elem,
            darkMode: document.documentElement.classList.contains('dark-mode'),
            textDirection: this.$opts.textDirection,
            drawioUrl: this.getDrawIoUrl(),
            pageId: Number(this.$opts.pageId),
            translations: {
                imageUploadErrorText: this.$opts.imageUploadErrorText,
                serverUploadLimitText: this.$opts.serverUploadLimitText,
            },
            translationMap: window.editor_translations,
            listenToCommonEvents: false,
        };

        const stored = this.parseStoredContent(this.input.value, this.legacyInput?.value || '');
        this.subtitle = stored.subtitle;
        this.sections = stored.sections;

        this.elem.addEventListener('click', this.handleClick.bind(this));
        this.elem.addEventListener('input', this.handleInput.bind(this));
        this.subtitleInput.addEventListener('input', () => {
            this.subtitle = this.subtitleInput.value;
            window.$events.emit('editor-html-change', '');
        });
        this.input.form.addEventListener('submit', () => {
            this.input.value = this.getSerializedHtml();
        });

        this.render();
    }

    getDrawIoUrl() {
        const drawioUrlElem = document.querySelector('[drawio-url]');
        if (drawioUrlElem) {
            return drawioUrlElem.getAttribute('drawio-url');
        }
        return '';
    }

    parseStoredContent(html, legacyJson) {
        const empty = {subtitle: '', sections: []};

        const parsedHtml = this.parseStoredHtml(html);
        if (parsedHtml !== null) {
            return parsedHtml;
        }

        const parsedLegacy = this.parseLegacyJson(legacyJson);
        if (parsedLegacy !== null) {
            return parsedLegacy;
        }

        const parsedLegacyRenderedHtml = this.parseLegacyRenderedHtml(html);
        if (parsedLegacyRenderedHtml !== null) {
            return parsedLegacyRenderedHtml;
        }

        if (!html || html.trim() === '') {
            return empty;
        }

        return {
            subtitle: '',
            sections: [{
                ...createGenericSection(),
                data: {html},
            }],
        };
    }

    parseStoredHtml(html) {
        if (!html || html.trim() === '') {
            return null;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const layout = doc.body.querySelector('.cofe-section-layout[data-cofe-section-builder="true"]');

        if (!layout) {
            return null;
        }

        const subtitleNode = layout.querySelector(':scope > .cofe-section-layout__subtitle');
        const sections = [...layout.querySelectorAll(':scope > .cofe-section-layout__section')].map(sectionEl => {
            const type = sectionEl.getAttribute('data-section-type');
            if (type === 'section') {
                return {
                    ...createColouredSection(),
                    id: sectionEl.getAttribute('data-section-id') || randomId(),
                    data: {
                        title: sectionEl.querySelector('.cofe-page-section__title')?.textContent?.trim() || '',
                        titleFootnote: sectionEl.getAttribute('data-title-footnote') || '',
                        color: sectionEl.getAttribute('data-color') || '1',
                        excerpt: sectionEl.querySelector('.cofe-page-section__excerpt')?.innerHTML?.trim() || '',
                        html: sectionEl.querySelector('.cofe-page-section__body')?.innerHTML?.trim() || '',
                        isMinimised: sectionEl.getAttribute('data-is-minimised') === 'true',
                    },
                };
            }

            return {
                ...createGenericSection(),
                id: sectionEl.getAttribute('data-section-id') || randomId(),
                data: {
                    html: sectionEl.querySelector('.cofe-page-section__body')?.innerHTML?.trim() || '',
                },
            };
        });

        return {
            subtitle: subtitleNode?.textContent?.trim() || '',
            sections,
        };
    }

    parseLegacyRenderedHtml(html) {
        if (!html || html.trim() === '') {
            return null;
        }

        if (!html.includes('comp-section') && !html.includes('comp-general')) {
            return null;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const sectionEls = [...doc.body.querySelectorAll('section.comp-section, section.comp-general')];

        if (sectionEls.length === 0) {
            return null;
        }

        const sections = sectionEls.map(sectionEl => {
            if (sectionEl.classList.contains('comp-section')) {
                return {
                    ...createColouredSection(),
                    id: this.extractLegacySectionId(sectionEl),
                    data: {
                        title: sectionEl.querySelector('.comp-section_intro h2')?.textContent?.trim() || '',
                        titleFootnote: '',
                        color: this.extractLegacySectionColor(sectionEl),
                        excerpt: this.extractLegacySectionExcerpt(sectionEl),
                        html: sectionEl.querySelector('.comp-section_article')?.innerHTML?.trim() || '',
                        isMinimised: sectionEl.classList.contains('-is-minimised'),
                    },
                };
            }

            return {
                ...createGenericSection(),
                id: this.extractLegacySectionId(sectionEl),
                data: {
                    html: sectionEl.innerHTML.trim(),
                },
            };
        });

        return {
            subtitle: '',
            sections,
        };
    }

    parseLegacyJson(legacyJson) {
        if (!legacyJson || legacyJson.trim() === '') {
            return null;
        }

        let parsed;
        try {
            parsed = JSON.parse(legacyJson);
        } catch (error) {
            return null;
        }

        if (Array.isArray(parsed)) {
            parsed = {subtitle: '', sections: parsed};
        }

        if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.sections)) {
            return null;
        }

        return {
            subtitle: typeof parsed.subtitle === 'string' ? parsed.subtitle : '',
            sections: parsed.sections.map(section => this.mapLegacySection(section)).filter(Boolean),
        };
    }

    mapLegacySection(section) {
        if (!section || typeof section !== 'object' || !section.data || typeof section.data !== 'object') {
            return null;
        }

        if (section.type === 'section') {
            return {
                ...createColouredSection(),
                id: typeof section.id === 'string' && section.id ? section.id : randomId(),
                minimised: Boolean(section.minimised),
                showOptions: false,
                data: {
                    title: section.data.title || '',
                    titleFootnote: section.data.titleFootnote || section.data.title_footnote || '',
                    color: String(section.data.color ?? '1'),
                    excerpt: section.data.excerpt || '',
                    html: section.data.html || '',
                    isMinimised: Boolean(section.data.isMinimised ?? section.data.is_minimised ?? true),
                },
            };
        }

        return {
            ...createGenericSection(),
            id: typeof section.id === 'string' && section.id ? section.id : randomId(),
            minimised: Boolean(section.minimised),
            showOptions: false,
            data: {
                html: section.data.html || '',
            },
        };
    }

    extractLegacySectionId(sectionEl) {
        const headingId = sectionEl.querySelector('.comp-section_intro h2')?.id || '';
        const articleId = sectionEl.querySelector('.comp-section_article')?.id || '';
        const hideName = sectionEl.getAttribute('hide-name') || '';
        const rawId = headingId || articleId || hideName;

        if (!rawId) {
            return randomId();
        }

        return rawId
            .replace(/^bkmrk-/, '')
            .replace(/-H\d$/i, '')
            .replace(/^comp-section-article-/, '')
            .replace(/^my-hider-/, '') || randomId();
    }

    extractLegacySectionColor(sectionEl) {
        const docColor = sectionEl.getAttribute('data-doc-color') || '';
        const docColorMatch = docColor.match(/theme-(\d+)/);
        if (docColorMatch) {
            return docColorMatch[1];
        }

        const introClasses = sectionEl.querySelector('.comp-section_intro-container')?.className || '';
        const introClassMatch = introClasses.match(/bg-theme-(\d+)/);
        if (introClassMatch) {
            return introClassMatch[1];
        }

        return '1';
    }

    extractLegacySectionExcerpt(sectionEl) {
        const intro = sectionEl.querySelector('.comp-section_intro');
        if (!intro) {
            return '';
        }

        const clone = intro.cloneNode(true);
        clone.querySelector('h2')?.remove();

        return clone.innerHTML.trim();
    }

    render() {
        this.destroyEditors();
        this.subtitleInput.value = this.subtitle;
        this.emptyMessage.style.display = this.sections.length > 0 ? 'none' : 'block';
        this.sectionsContainer.innerHTML = this.sections.map((section, index) => this.renderSection(section, index)).join('');
        this.initEditors();
    }

    renderSection(section, index) {
        const isGeneric = section.type === 'generic';
        const colorOptions = [
            ['1', 'Pink'],
            ['2', 'Yellow'],
            ['3', 'Purple'],
            ['4', 'Green'],
            ['5', 'Light Green'],
            ['0', 'Blue'],
        ].map(([value, label]) => {
            const selected = section.data.color === value ? ' selected' : '';
            return `<option value="${value}"${selected}>${label}</option>`;
        }).join('');

        return `
            <section class="cofe-section-editor__card" data-section-id="${section.id}">
                <div class="cofe-section-editor__card-toolbar">
                    <div class="cofe-section-editor__card-label">${isGeneric ? 'Generic Section' : 'Coloured Section'}</div>
                    <div class="cofe-section-editor__card-actions">
                        <button type="button" class="button outline small" data-action="move-up" ${index === 0 ? 'disabled' : ''}>Up</button>
                        <button type="button" class="button outline small" data-action="move-down" ${index === this.sections.length - 1 ? 'disabled' : ''}>Down</button>
                        <button type="button" class="button outline small" data-action="toggle-minimise">${section.minimised ? 'Expand' : 'Collapse'}</button>
                        <button type="button" class="button outline negative small" data-action="delete">Delete</button>
                    </div>
                </div>

                ${isGeneric ? '' : `
                    <div class="cofe-section-editor__meta">
                        <input type="text"
                               class="cofe-section-editor__field"
                               data-field="title"
                               value="${escapeHtml(section.data.title)}"
                               placeholder="${escapeHtml(this.$opts.titlePlaceholder)}">

                        <button type="button" class="cofe-section-editor__options-toggle" data-action="toggle-options">
                            ${section.showOptions ? escapeHtml(this.$opts.hideOptionsText) : escapeHtml(this.$opts.showOptionsText)}
                        </button>

                        <div class="cofe-section-editor__options ${section.showOptions ? 'is-open' : ''}">
                            <div class="cofe-section-editor__options-grid">
                                <input type="text"
                                       class="cofe-section-editor__field"
                                       data-field="titleFootnote"
                                       value="${escapeHtml(section.data.titleFootnote)}"
                                       placeholder="${escapeHtml(this.$opts.titleFootnotePlaceholder)}">

                                <select class="cofe-section-editor__field" data-field="color">
                                    ${colorOptions}
                                </select>
                            </div>

                            <label class="cofe-section-editor__checkbox">
                                <input type="checkbox" data-field="isMinimised" ${section.data.isMinimised ? 'checked' : ''}>
                                <span>${escapeHtml(this.$opts.minimisedLabel)}</span>
                            </label>

                            <label class="cofe-section-editor__excerpt-label">${escapeHtml(this.$opts.excerptLabel)}</label>
                            <textarea id="cofe-section-excerpt-${section.id}"
                                      class="cofe-section-editor__textarea"
                                      data-editor-role="excerpt">${escapeHtml(section.data.excerpt)}</textarea>
                        </div>
                    </div>
                `}

                <div class="cofe-section-editor__body ${section.minimised ? 'is-hidden' : ''}">
                    <textarea id="cofe-section-body-${section.id}"
                              class="cofe-section-editor__textarea"
                              data-editor-role="body">${escapeHtml(section.data.html)}</textarea>
                </div>
            </section>
        `;
    }

    async initEditors() {
        for (const section of this.sections) {
            const bodyEditor = await this.createEditor(`cofe-section-body-${section.id}`, {height: 280});
            const sectionEditor = {body: bodyEditor};

            if (section.type === 'section') {
                sectionEditor.excerpt = await this.createEditor(`cofe-section-excerpt-${section.id}`, {
                    height: 140,
                    toolbar: 'undo redo | bold italic underline | link | removeformat',
                    plugins: 'link autolink paste',
                });
            }

            this.editorMap.set(section.id, sectionEditor);
        }
    }

    async createEditor(textareaId, overrides = {}) {
        const textarea = document.getElementById(textareaId);
        if (!textarea) {
            return null;
        }

        const config = {
            ...buildEditorConfig(this.tinyMceOptions),
            target: textarea,
            selector: undefined,
            height: overrides.height || 320,
        };

        if (overrides.toolbar) {
            config.toolbar = overrides.toolbar;
        }

        if (overrides.plugins) {
            config.plugins = overrides.plugins;
        }

        const editors = await window.tinymce.init(config);
        return editors[0];
    }

    destroyEditors() {
        for (const editors of this.editorMap.values()) {
            if (editors.excerpt) {
                editors.excerpt.remove();
            }
            if (editors.body) {
                editors.body.remove();
            }
        }
        this.editorMap.clear();
    }

    handleClick(event) {
        const button = event.target.closest('[data-action]');
        if (!button) {
            return;
        }

        event.preventDefault();
        const sectionEl = button.closest('[data-section-id]');
        const sectionId = sectionEl?.getAttribute('data-section-id') || '';
        const action = button.getAttribute('data-action');
        const index = this.sections.findIndex(section => section.id === sectionId);

        if (index === -1) {
            return;
        }

        this.syncSectionStateFromEditors();

        if (action === 'move-up' && index > 0) {
            [this.sections[index - 1], this.sections[index]] = [this.sections[index], this.sections[index - 1]];
        }

        if (action === 'move-down' && index < this.sections.length - 1) {
            [this.sections[index + 1], this.sections[index]] = [this.sections[index], this.sections[index + 1]];
        }

        if (action === 'toggle-minimise') {
            this.sections[index].minimised = !this.sections[index].minimised;
        }

        if (action === 'toggle-options') {
            this.sections[index].showOptions = !this.sections[index].showOptions;
        }

        if (action === 'delete') {
            const confirmed = window.confirm(this.$opts.deleteConfirmText);
            if (!confirmed) {
                return;
            }
            this.sections.splice(index, 1);
        }

        this.render();
        window.$events.emit('editor-html-change', '');
    }

    handleInput(event) {
        const sectionEl = event.target.closest('[data-section-id]');
        const field = event.target.getAttribute('data-field');
        if (!sectionEl || !field) {
            return;
        }

        const section = this.sections.find(item => item.id === sectionEl.getAttribute('data-section-id'));
        if (!section) {
            return;
        }

        if (event.target.type === 'checkbox') {
            section.data[field] = event.target.checked;
        } else {
            section.data[field] = event.target.value;
        }

        window.$events.emit('editor-html-change', '');
    }

    syncSectionStateFromEditors() {
        for (const section of this.sections) {
            const editors = this.editorMap.get(section.id);
            if (!editors) {
                continue;
            }

            if (editors.body) {
                section.data.html = editors.body.getContent();
            }

            if (editors.excerpt) {
                section.data.excerpt = editors.excerpt.getContent();
            }
        }
    }

    addSection(type) {
        this.syncSectionStateFromEditors();
        this.subtitle = this.subtitleInput.value;
        this.sections.push(type === 'section' ? createColouredSection() : createGenericSection());
        this.render();
        window.$events.emit('editor-html-change', '');
    }

    serializeSection(section) {
        const sectionEl = document.createElement('section');
        sectionEl.className = `cofe-section-layout__section cofe-page-section ${section.type === 'section' ? 'cofe-page-section--coloured' : 'cofe-page-section--generic'}`;
        sectionEl.setAttribute('data-section-type', section.type);
        sectionEl.setAttribute('data-section-id', section.id);

        if (section.type === 'section') {
            sectionEl.setAttribute('data-color', section.data.color);
            sectionEl.setAttribute('data-title-footnote', section.data.titleFootnote || '');
            sectionEl.setAttribute('data-is-minimised', section.data.isMinimised ? 'true' : 'false');

            const header = document.createElement('div');
            header.className = `cofe-page-section__header cofe-page-section__header--${section.data.color}`;

            const intro = document.createElement('div');
            intro.className = 'cofe-page-section__intro';

            const title = document.createElement('h2');
            title.className = 'cofe-page-section__title';
            title.textContent = section.data.title || 'Untitled Section';
            intro.append(title);

            if (section.data.titleFootnote) {
                const footnote = document.createElement('p');
                footnote.className = 'cofe-page-section__title-footnote';
                footnote.textContent = section.data.titleFootnote;
                intro.append(footnote);
            }

            if (section.data.excerpt) {
                const excerpt = document.createElement('div');
                excerpt.className = 'cofe-page-section__excerpt';
                excerpt.innerHTML = section.data.excerpt;
                intro.append(excerpt);
            }

            const toggle = document.createElement('button');
            toggle.className = 'cofe-page-section__toggle';
            toggle.type = 'button';
            toggle.setAttribute('aria-expanded', section.data.isMinimised ? 'false' : 'true');
            toggle.innerHTML = `
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"></path>
                </svg>
            `;

            header.append(intro);
            header.append(toggle);
            sectionEl.append(header);
        }

        const body = document.createElement('div');
        body.className = 'cofe-page-section__body';
        body.innerHTML = section.data.html;
        sectionEl.append(body);

        return sectionEl;
    }

    getSerializedHtml() {
        this.syncSectionStateFromEditors();
        this.subtitle = this.subtitleInput.value;

        const layout = document.createElement('div');
        layout.className = 'cofe-section-layout';
        layout.setAttribute('data-cofe-section-builder', 'true');

        if (this.subtitle.trim()) {
            const subtitle = document.createElement('div');
            subtitle.className = 'cofe-section-layout__subtitle';
            subtitle.textContent = this.subtitle.trim();
            layout.append(subtitle);
        }

        this.sections.forEach(section => {
            layout.append(this.serializeSection(section));
        });

        return layout.innerHTML.trim() === '' ? '' : layout.outerHTML;
    }

    async getContent() {
        const html = this.getSerializedHtml();
        this.input.value = html;
        return {html};
    }
}
